import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaLock, FaFileAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "September 2026";

  return (
    <div className="min-h-screen bg-gradient-dark">
      <Navbar />
      <div className="py-20 px-4 sm:px-6 lg:px-8 pt-28">

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <FaShieldAlt className="text-6xl text-emerald-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Privacy Policy</span>
          </h1>
          <p className="text-gray-400">Last Updated: {lastUpdated}</p>
          <p className="text-gray-400 mt-2">Apex Legal Systems LLC</p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12 space-y-8 text-gray-300 leading-relaxed"
        >
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <FaFileAlt className="text-emerald-400 mr-3" />
              Introduction
            </h2>
            <p>
              Apex Legal Systems LLC ("we," "our," or "us") is committed to protecting your privacy and maintaining the security of your information. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you visit our website <strong className="text-white">ApexLegalSystems.com</strong> or use our services.
            </p>
            <p className="mt-4">
              As a provider of HIPAA-compliant AI-powered intake solutions for personal injury law firms, we handle Protected Health Information (PHI) with the utmost care and in strict accordance with HIPAA regulations.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">1.1 Website Visitor Information</h3>
            <p>When you visit our website, we may collect:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Personal Information:</strong> Name, email address, phone number, law firm name, and number of attorneys when you submit a contact form or book a discovery call</li>
              <li><strong className="text-white">Usage Data:</strong> IP address, browser type, device information, pages visited, time spent on pages, and referring URLs</li>
              <li><strong className="text-white">Cookies and Tracking:</strong> We use cookies and similar technologies to enhance user experience and analyze website traffic</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">1.2 Client Data (For Active Clients Only)</h3>
            <p>When you become a client and use our services, we process:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Account Information:</strong> Law firm details, authorized users, billing information, and contact preferences</li>
              <li><strong className="text-white">Protected Health Information (PHI):</strong> Information collected through your intake forms, including potential client names, contact information, injury details, and medical information</li>
              <li><strong className="text-white">Communication Data:</strong> Call recordings, SMS messages, emails, and chat transcripts between your firm and potential clients</li>
              <li><strong className="text-white">Technical Data:</strong> System logs, API usage, and performance metrics</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
            
            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">2.1 Website Visitors</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to your inquiries and schedule discovery calls</li>
              <li>To send marketing communications (you can opt out anytime)</li>
              <li>To improve our website and services</li>
              <li>To analyze website traffic and user behavior</li>
            </ul>

            <h3 className="text-xl font-semibold text-emerald-400 mt-6 mb-3">2.2 Client Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide our AI-powered intake services</li>
              <li>To process and route leads to your law firm</li>
              <li>To generate analytics and reports on your intake performance</li>
              <li>To provide customer support and training</li>
              <li>To comply with legal obligations and enforce our Terms of Service</li>
              <li>To improve our AI models and system performance (using de-identified data only)</li>
            </ul>
          </section>

          {/* HIPAA Compliance */}
          <section className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <FaLock className="text-emerald-400 mr-3" />
              3. HIPAA Compliance
            </h2>
            <p>
              <strong className="text-white">We are a HIPAA-compliant Business Associate.</strong> For all clients handling Protected Health Information (PHI):
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>We sign a <strong className="text-white">Business Associate Agreement (BAA)</strong> before processing any PHI</li>
              <li>All PHI is encrypted at rest (AES-256) and in transit (TLS 1.2+)</li>
              <li>Access to PHI is restricted to authorized personnel only</li>
              <li>We maintain comprehensive audit logs of all PHI access</li>
              <li>We conduct regular security assessments and risk analyses</li>
              <li>All third-party vendors handling PHI sign BAAs with us</li>
              <li>We have incident response procedures for any suspected breaches</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. How We Share Your Information</h2>
            
            <p className="mb-4"><strong className="text-white">We do NOT sell your personal information.</strong></p>
            
            <p>We may share information with:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Service Providers:</strong> Third-party vendors who help us operate our services (all sign BAAs for PHI)</li>
              <li><strong className="text-white">Your Law Firm:</strong> Client leads and intake data are shared with you as the client</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or to protect our rights</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with continued HIPAA compliance)</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
            <p>We implement industry-standard security measures:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>AES-256 encryption for data at rest</li>
              <li>TLS 1.2+ encryption for data in transit</li>
              <li>Multi-factor authentication for system access</li>
              <li>Regular security audits and penetration testing</li>
              <li>Employee training on HIPAA and data security</li>
              <li>Secure data centers with strong security practices</li>
              <li>Automated backup and disaster recovery procedures</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Website Visitor Data:</strong> Retained for 2 years or until you request deletion</li>
              <li><strong className="text-white">Client Account Data:</strong> Retained for the duration of your subscription plus 7 years (as required by legal and tax regulations)</li>
              <li><strong className="text-white">PHI:</strong> Retained per your instructions and HIPAA requirements (minimum 6 years from creation or last use)</li>
              <li><strong className="text-white">Backup Data:</strong> May be retained in backups for up to 90 days after deletion</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Access:</strong> Request a copy of your personal information</li>
              <li><strong className="text-white">Correction:</strong> Request correction of inaccurate information</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
              <li><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong className="text-white">Data Portability:</strong> Request your data in a portable format</li>
              <li><strong className="text-white">HIPAA Rights:</strong> All rights under HIPAA, including breach notification</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at: <a href="mailto:contact@apexlegalsystems.com" className="text-emerald-400 hover:text-emerald-300">contact@apexlegalsystems.com</a>
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Cookies and Tracking Technologies</h2>
            <p>We use cookies for:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Essential website functionality</li>
              <li>Analytics and performance monitoring (Google Analytics, PostHog)</li>
              <li>Marketing and advertising (retargeting)</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* International Users */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. International Users</h2>
            <p>
              Our services are based in the United States. If you access our services from outside the US, your information will be transferred to, stored, and processed in the United States. By using our services, you consent to this transfer.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Posting the updated policy on our website</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending email notifications to active clients</li>
            </ul>
            <p className="mt-4">
              Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-slate-700/50 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
            <p className="mb-4">
              If you have questions about this Privacy Policy or our data practices, contact us:
            </p>
            <div className="space-y-2">
              <p><strong className="text-white">Apex Legal Systems LLC</strong></p>
              <p>[Your Wyoming Registered Agent Address]</p>
              <p>Email: <a href="mailto:contact@apexlegalsystems.com" className="text-emerald-400 hover:text-emerald-300">contact@apexlegalsystems.com</a></p>
              <p>Website: <a href="https://ApexLegalSystems.com" className="text-emerald-400 hover:text-emerald-300">ApexLegalSystems.com</a></p>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              For HIPAA-related concerns or to report a potential breach, please contact us immediately at the email above with "URGENT: HIPAA" in the subject line.
            </p>
          </section>
        </motion.div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
